import { useMutationState } from '@tanstack/react-query';

import { isClientError } from './is-client-error';
import { isVisibleInOfflineHistory } from './mutation-offline-history';
import { MutationWithContext } from './mutations';

const sortBySubmittedAt = (a: MutationWithContext, b: MutationWithContext) => {
  const aDate = a.state.submittedAt;
  const bDate = b.state.submittedAt;
  if (aDate && bDate) {
    return bDate - aDate;
  }
  if (aDate && !bDate) {
    return -1;
  }
  if (!aDate && bDate) {
    return 1;
  }
  return 0;
};

export const useMutations = () => {
  /**
   * These are mutations that are currently running and actively waiting for a response.
   */
  const pendingMutations = useMutationState({
    filters: {
      status: 'pending',
      mutationKey: [],
      predicate: (mutation) => {
        return (
          !mutation.state.isPaused &&
          isVisibleInOfflineHistory(mutation.options.mutationKey, mutation)
        );
      },
    },
    select: (mutation) => mutation as MutationWithContext,
  }).sort(sortBySubmittedAt);

  /**
   * These are mutations that are currently paused and waiting to be resumed.
   */
  const pausedMutations = useMutationState({
    filters: {
      status: 'pending',
      mutationKey: [],
      predicate: (mutation) => {
        return (
          mutation.state.isPaused &&
          isVisibleInOfflineHistory(mutation.options.mutationKey, mutation)
        );
      },
    },
    select: (mutation) => mutation as MutationWithContext,
  }).sort(sortBySubmittedAt);

  /**
   * These are mutations that have failed with a non 4xx error.
   */
  const failedMutations = useMutationState({
    filters: {
      status: 'error',
      mutationKey: [],
      predicate: (mutation) => {
        return (
          !isClientError(mutation.state.error) &&
          isVisibleInOfflineHistory(mutation.options.mutationKey, mutation)
        );
      },
    },
    select: (mutation) => mutation as MutationWithContext,
  }).sort(sortBySubmittedAt);

  /**
   * These are mutations that succeeded
   */
  const succeededMutations = useMutationState({
    filters: {
      status: 'success',
      mutationKey: [],
      predicate: (mutation) => {
        return (
          !mutation.state.isPaused &&
          isVisibleInOfflineHistory(mutation.options.mutationKey, mutation)
        );
      },
    },
    select: (mutation) => mutation as MutationWithContext,
  }).sort(sortBySubmittedAt);

  const hasPendingMutations = pendingMutations.length > 0;
  const hasPausedMutations = pausedMutations.length > 0;
  const hasFailedMutations = failedMutations.length > 0;
  const hasSucceededMutations = succeededMutations.length > 0;

  const mutations = [
    ...pendingMutations,
    ...pausedMutations,
    ...failedMutations,
    ...succeededMutations,
  ];

  const affectedFields = Array.from(
    new Set(
      mutations
        .map((mutation) => mutation.state.context?.metadata?.fieldId)
        .filter((fieldId) => typeof fieldId !== 'undefined')
    )
  ).filter((fieldId) => fieldId !== undefined);

  return {
    affectedFields,
    mutations,
    hasFailedMutations,
    hasPausedMutations,
    hasPendingMutations,
    hasSucceededMutations,
    succeededMutations,
    pendingMutations,
    pausedMutations,
    failedMutations,
  };
};
